import React from 'react';
import { LucideIcon, Banknote, BanknoteX, PackageCheck, PackageX } from 'lucide-react';

export interface StatusBadgeProps {
  estado: string;
  tipo: 'actividad' | 'pago' | 'entrega';
  icon?: LucideIcon;
  className?: string;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  estado,
  tipo,
  icon: Icon,
  className = ''
}) => {
  const getBadgeConfig = () => {
      switch (tipo) {
        case 'actividad': {
          const actividadBadges = {
            planificacion: {
              text: 'Planificación',
              classes: 'bg-blue-100 text-blue-700',
              icon: undefined
            },
            en_curso: {
              text: 'En Curso',
              classes: 'bg-green-100 text-green-700',
              icon: undefined
            },
            finalizada: {
              text: 'Finalizada',
              classes: 'bg-slate-100 text-slate-700',
              icon: undefined
            },
            archivada: {
              text: 'Archivada',
              classes: 'bg-amber-100 text-amber-700',
              icon: undefined
            }
          };
          return actividadBadges[estado as keyof typeof actividadBadges] || actividadBadges.planificacion;
        }

        case 'pago': {
          const pagoBadges = {
            no_pagado: {
              text: 'No Pagado',
              classes: 'bg-orange-100 text-orange-700',
              icon: BanknoteX
            },
            pago_parcial: {
              text: 'Pago Parcial',
              classes: 'bg-yellow-100 text-yellow-700',
              icon: Banknote
            },
            pagado: {
              text: 'Pagado',
              classes: 'bg-green-100 text-green-700',
              icon: Banknote
            }
          };
          return pagoBadges[estado as keyof typeof pagoBadges] || pagoBadges.no_pagado;
        }

        case 'entrega': {
          const entregaBadges = {
            no_entregado: {
              text: 'No Entregado',
              classes: 'bg-orange-100 text-orange-700',
              icon: PackageX
            },
            entrega_parcial: {
              text: 'Entrega Parcial',
              classes: 'bg-yellow-100 text-yellow-700',
              icon: PackageCheck
            },
            entregado: {
              text: 'Entregado',
              classes: 'bg-green-100 text-green-700',
              icon: PackageCheck
            }
          };
          return entregaBadges[estado as keyof typeof entregaBadges] || entregaBadges.no_entregado;
        }

        default:
          return {
            text: 'Desconocido',
            classes: 'bg-gray-100 text-gray-700',
            icon: undefined
          };
      }
    }

  const config = getBadgeConfig();

  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${config.classes} ${className}`}>
      {(Icon || config.icon) && (Icon ? <Icon className="w-3 h-3" /> : React.createElement(config.icon!, { className: "w-3 h-3" }))}
      {config.text}
    </span>
  );
};

// Badge compacto para uso en espacios reducidos
export interface StatusBadgeCompactProps {
  estado: string;
  tipo: 'actividad' | 'pago' | 'entrega';
  className?: string;
}

export const StatusBadgeCompact: React.FC<StatusBadgeCompactProps> = ({
  estado,
  tipo,
  className = ''
}) => {
  const getBadgeConfig = () => {
    switch (tipo) {
      case 'actividad': {
        const actividadBadges = {
          planificacion: {
            text: 'Planificación',
            classes: 'bg-blue-100 text-blue-700',
            icon: undefined
          },
          en_curso: {
            text: 'En Curso',
            classes: 'bg-green-100 text-green-700',
            icon: undefined
          },
          finalizada: {
            text: 'Finalizada',
            classes: 'bg-slate-100 text-slate-700',
            icon: undefined
          },
          archivada: {
            text: 'Archivada',
            classes: 'bg-amber-100 text-amber-700',
            icon: undefined
          }
        };
        return actividadBadges[estado as keyof typeof actividadBadges] || actividadBadges.planificacion;
      }

      case 'pago': {
        const pagoBadges = {
          no_pagado: {
            text: 'No Pagado',
            classes: 'bg-orange-100 text-orange-700',
            icon: BanknoteX
          },
          pago_parcial: {
            text: 'Pago Parcial',
            classes: 'bg-yellow-100 text-yellow-700',
            icon: Banknote
          },
          pagado: {
            text: 'Pagado',
            classes: 'bg-green-100 text-green-700',
            icon: Banknote
          }
        };
        return pagoBadges[estado as keyof typeof pagoBadges] || pagoBadges.no_pagado;
      }

      case 'entrega': {
        const entregaBadges = {
          no_entregado: {
            text: 'No Entregado',
            classes: 'bg-orange-100 text-orange-700',
            icon: PackageX
          },
          entrega_parcial: {
            text: 'Entrega Parcial',
            classes: 'bg-yellow-100 text-yellow-700',
            icon: PackageCheck
          },
          entregado: {
            text: 'Entregado',
            classes: 'bg-green-100 text-green-700',
            icon: PackageCheck
          }
        };
        return entregaBadges[estado as keyof typeof entregaBadges] || entregaBadges.no_entregado;
      }

      default:
        return {
          text: 'Desconocido',
          classes: 'bg-gray-100 text-gray-700',
          icon: undefined
        };
    }
  };

  const config = getBadgeConfig();

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold ${config.classes} ${className}`}>
      {config.icon && React.createElement(config.icon, { className: "w-3 h-3" })}
      {config.text}
    </span>
  );
};
